// Keeps the slider pagination dots capped at a fixed window size.
//
// Swiper renders one bullet per slide, so long lists turn the pagination row
// into a long line of dots. Instead we show a fixed window of at most
// `DOT_GROUP_SIZE` dots (the Instagram-style pattern): the active dot moves
// inside the window, and once it reaches an edge the window slides by one so
// the visible dot count never changes while swiping. Edge dots are shrunk to
// hint that more items exist in that direction.
//
// The active dot is computed here from the Swiper state rather than read back
// from Swiper's classes: Swiper only refreshes its bullets on snap index /
// snap grid changes, so loop re-ordering, real index changes and slide count
// changes could leave the highlighted dot stale.

export const DOT_GROUP_SIZE = 5;

const ACTIVE_BULLET_CLASS = "swiper-pagination-bullet-active";
const EDGE_BULLET_CLASS = "swiper-pagination-bullet-edge";

// Explicit inline display is used instead of clearing it, because globals.css
// hides every dot past the 5th by default. Inline styles must therefore state
// visibility explicitly for the dots inside the active window.
const SHOWN = "inline-block";
const HIDDEN = "none";

// Every event after which the active slide, the slide list or the bullets
// themselves may have changed.
const SYNC_EVENTS = [
  "slideChange",
  "realIndexChange",
  "activeIndexChange",
  "snapIndexChange",
  "slidesLengthChange",
  "snapGridLengthChange",
  "transitionEnd",
  "touchEnd",
  "loopFix",
  "update",
  "resize",
  "breakpoint",
  "paginationRender",
  "paginationUpdate",
].join(" ");

// Mirrors how Swiper's pagination picks its current bullet.
function getActiveIndex(swiper) {
  const { params } = swiper;
  if (params.loop) {
    const perGroup = params.slidesPerGroup > 1 ? params.slidesPerGroup : 1;
    return Math.floor((swiper.realIndex || 0) / perGroup);
  }
  if (typeof swiper.snapIndex === "number") return swiper.snapIndex;
  return swiper.activeIndex || 0;
}

function setDisplay(bullet, display) {
  if (bullet.style.display !== display) bullet.style.display = display;
}

// Returns the first visible dot index so `current` stays inside the window,
// keeping one dot of look-ahead on either side while more dots exist there.
export function nextWindowStart(start, current, total, size) {
  if (total <= size) return 0;
  let next = Math.min(Math.max(start, 0), total - size);
  if (current < next + 1) next = current - 1;
  else if (current > next + size - 2) next = current - size + 2;
  return Math.min(Math.max(next, 0), total - size);
}

// Highlights the active bullet and shows the window that contains it.
// `itemCount` is the number of real items: sliders that duplicate their
// slides for a smooth loop pass it so the extra bullets are hidden and the
// duplicates map back onto the real item's dot. Returns the window start so
// the caller can carry it into the next sync.
export function syncDotWindow(
  paginationEl,
  activeIndex,
  { itemCount, groupSize = DOT_GROUP_SIZE, start = 0 } = {},
) {
  if (!paginationEl) return start;
  const bullets = paginationEl.children;
  const rendered = bullets.length;
  if (!rendered) return start;

  const total =
    itemCount > 0 && itemCount < rendered ? itemCount : rendered;
  const size = Math.min(groupSize, total);
  const current = Math.min(Math.max(activeIndex, 0) % total, total - 1);
  const first = nextWindowStart(start, current, total, size);
  const last = first + size - 1;

  for (let i = 0; i < rendered; i += 1) {
    const bullet = bullets[i];
    const visible = i < total && i >= first && i <= last;
    const isEdge =
      visible &&
      i !== current &&
      ((i === first && first > 0) || (i === last && last < total - 1));
    bullet.classList.toggle(ACTIVE_BULLET_CLASS, i === current);
    bullet.classList.toggle(EDGE_BULLET_CLASS, isEdge);
    setDisplay(bullet, visible ? SHOWN : HIDDEN);
  }
  return first;
}

// Applies the window now and keeps it in sync with the Swiper instance.
// `itemCount` (a number or a getter) only matters for loop sliders that
// render their items twice. Returns a cleanup function.
export function bindDotWindow(
  swiper,
  paginationEl,
  { itemCount, groupSize = DOT_GROUP_SIZE } = {},
) {
  if (!swiper || !paginationEl) return () => {};

  let frame = null;
  let start = 0;
  const run = () => {
    if (swiper.destroyed) return;
    const count = typeof itemCount === "function" ? itemCount() : itemCount;
    start = syncDotWindow(paginationEl, getActiveIndex(swiper), {
      itemCount: swiper.params.loop ? count : undefined,
      groupSize,
      start,
    });
  };
  // Some events fire mid-update (e.g. before loop re-ordering settles), so
  // re-check once more on the next frame.
  const schedule = () => {
    run();
    if (typeof requestAnimationFrame !== "function") return;
    if (frame !== null) cancelAnimationFrame(frame);
    frame = requestAnimationFrame(() => {
      frame = null;
      run();
    });
  };

  swiper.on(SYNC_EVENTS, schedule);
  schedule();

  return () => {
    swiper.off(SYNC_EVENTS, schedule);
    if (frame !== null) cancelAnimationFrame(frame);
  };
}
